import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BoothRulesPanel from './BoothRulesPanel';
import BoothShoppingCart from './BoothShoppingCart';
import { 
  validateBoothRules, 
  applyAutoSuggestions,
  BOOTH_RULES_CONFIG
} from '../services/boothRulesService';
import { createCartItem, updateCartItemAction, loadActiveCartItems, logCartExpired } from '../services/cartItemsService';
import { reserveBoothRemote, releaseBoothRemote, loadReservationsForLayout, subscribeReservationsForLayout, subscribeMyReservationsForSession } from '../services/reservationsService';
import { listVendorsForUser } from '../services/vendorsService';
import { getUserProfile } from '../services/profilesService';
import './LayoutBuilder.css';
import CellTooltip from './CellTooltip.jsx';

const getLevelColor = (level, customLevels) =>
  customLevels?.[level]?.color || '#95a5a6';

const getLevelPricing = (level, isCorner, customLevels) => {
  const levelData = customLevels?.[level];
  if (!levelData) return undefined;
  return isCorner ? levelData.cornerPrice : levelData.inlinePrice;
};

const getLevelName = (level, customLevels) => {
  return customLevels?.[level]?.name || level;
};

const parsePositiveMinutes = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const GridCell = ({ cellData, onClick, isSelected, isRequired, cellSize, fontSize, customLevels, rulesConfig = {}, cartActionBusy = false }) => {
  const cellColor = getLevelColor(cellData.level, customLevels);

  const getTextColor = (backgroundColor) => {
    if (!backgroundColor) return '#333333';
    if (typeof backgroundColor === 'string' && backgroundColor.startsWith('#')) {
      let hex = backgroundColor.replace('#', '');
      if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128 ? '#333333' : '#ffffff';
    }
    if (typeof backgroundColor === 'string' && backgroundColor.toLowerCase().startsWith('hsl')) {
      try {
        const m = backgroundColor.match(/\b(\d{1,3})%\s*\)?$/);
        if (m && m[1]) return Number(m[1]) > 50 ? '#333333' : '#ffffff';
      } catch (e) {}
      return '#ffffff';
    }
    return '#333333';
  };

  // Generate a deterministic pastel color from a string (used for group badges)
  const hashStringToColor = (str) => {
    if (!str) return '#9aa0a6';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash; // Convert to 32bit int
    }
    const h = Math.abs(hash) % 360; // hue
    const s = 60 + (Math.abs(hash) % 20); // saturation 60-79
    const l = 50; // lightness
    return `hsl(${h} ${s}% ${l}%)`;
  };

  const borderColor = isSelected ? '#ffff00' : (isRequired ? '#ff4444' : (cellData.corner === 'Y' ? '#000000' : 'transparent'));
  const borderWidth = isRequired ? '4px' : (cellData.corner === 'Y' ? '3px' : '2px');

  const isInvisible = cellData.status === 'Invisible' || cellData.level === 'Invisible';
  const isOnHold = !isInvisible && cellData.status === 'On-Hold';
  const isReservedByMe = !isInvisible && cellData.status === 'Reserved' && cellData.reservedByMe;
  const isPendingByMe = !isInvisible && cellData.status === 'Pending' && cellData.pendingByMe;
  const isReservedByOther = !isInvisible && cellData.status === 'Reserved' && !cellData.reservedByMe;
  const isPendingByOther = !isInvisible && cellData.status === 'Pending' && !cellData.pendingByMe;

  // Tooltip/hover title per state (include booth number and status)
  const statusMessage = (
    isReservedByMe ? 'Reserved by you'
    : isReservedByOther ? 'Reserved by others'
    : isPendingByMe ? 'Pending by you'
    : isPendingByOther ? 'Pending by others'
    : isOnHold ? 'On-Hold'
    : 'Available'
  );
  // Include booth type/level and price in the hover tooltip
  const levelId = cellData.level || 'Unknown';
  const levelName = levelId === 'Unknown' ? 'Unknown' : getLevelName(levelId, customLevels);
  const isCornerCell = cellData.corner === 'Y' || cellData.corner === true;
  const priceVal = getLevelPricing(levelId, isCornerCell, customLevels);
  const formatPrice = (pCents) => {
    if (pCents === null || typeof pCents === 'undefined' || Number(pCents) === 0) return 'N/A';
    try {
      const dollars = Number(pCents) / 100;
      return dollars.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
    } catch {
      return `$${pCents}`;
    }
  };
  const priceLabel = formatPrice(priceVal);
  const hoverTitle = isInvisible ? undefined : `${cellData.number}: ${statusMessage} — ${levelName} — ${priceLabel}`;
  const ariaLabel = isInvisible ? undefined : `Booth ${cellData.number}. ${statusMessage}. Level ${levelName}. Corner ${isCornerCell ? 'Yes' : 'No'}. Price ${priceLabel}`;

  // Resolve group name from rulesConfig if groupid exists
  const resolveGroupName = () => {
    if (!cellData || !cellData.groupid) return 'No';
    try {
      const gid = String(cellData.groupid);
      const rules = Object.values(rulesConfig || {});
      for (const r of rules) {
        if (r && Array.isArray(r.groups)) {
          const g = r.groups.find(grp => String(grp.id) === gid);
          if (g) return g.name || 'Unnamed Group';
        }
      }
    } catch (e) {}
    return 'No';
  };
  const groupName = resolveGroupName();

  // Tooltip portal state and handlers
  const btnRef = useRef(null);
  const [ttVisible, setTtVisible] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const handleMouseEnter = () => {
    const r = btnRef.current && btnRef.current.getBoundingClientRect();
    if (r) setAnchorRect(r);
    setTtVisible(true);
  };
  const handleMouseMove = () => {
    const r = btnRef.current && btnRef.current.getBoundingClientRect();
    if (r) setAnchorRect(r);
  };
  const handleMouseLeave = () => setTtVisible(false);
  const handleFocus = () => {
    const r = btnRef.current && btnRef.current.getBoundingClientRect();
    if (r) setAnchorRect(r);
    setTtVisible(true);
  };
  const handleBlur = () => setTtVisible(false);

  return (
    <button
      ref={btnRef}
      className={`grid-cell ${isSelected ? 'selected' : ''} ${isRequired ? 'required-booth' : ''} ${cellData.status !== 'Available' ? 'unavailable-booth' : ''} ${isInvisible ? 'invisible-cell' : ''} ${isOnHold ? 'on-hold' : ''} ${isReservedByMe ? 'reserved-by-me' : ''} ${isPendingByMe ? 'pending-by-me' : ''} ${isReservedByOther ? 'reserved-by-other' : ''} ${isPendingByOther ? 'pending-by-other' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={(e) => onClick(cellData, e)}
      style={{
        backgroundColor: cellColor,
        borderColor,
        borderWidth,
        width: `${cellSize}px`,
        height: `${cellSize}px`,
        color: getTextColor(cellColor),
        cursor: cartActionBusy ? 'wait' : (cellData.status !== 'Available' ? 'not-allowed' : 'pointer')
      }}
      aria-label={ariaLabel}
    >
      {/* Tooltip (portal) */}
      <CellTooltip visible={ttVisible && !isInvisible} anchorRect={anchorRect}>
        <span className="tt-line"><strong>Booth:</strong> {cellData.number}</span>
        <span className="tt-line"><strong>Status:</strong> {statusMessage}</span>
        <span className="tt-line"><strong>Level:</strong> {levelName}</span>
        <span className="tt-line"><strong>Corner:</strong> {isCornerCell ? 'Yes' : 'No'}</span>
        <span className="tt-line"><strong>Price:</strong> {priceLabel}</span>
        <span className="tt-line"><strong>Group:</strong> {groupName === 'No' ? 'No' : groupName}</span>
      </CellTooltip>
      <div className="cell-content" style={{ position: 'relative' }}>
        <div className="cell-number" style={{ fontSize: `${fontSize}px` }}>{cellData.number}</div>
        {cellData && cellData.groupid && (
          <div
            className="group-badge"
            title={`Group: ${cellData.groupid}`}
            style={{
              position: 'absolute',
              left: '4px',
              bottom: '4px',
              width: Math.max(8, Math.floor(cellSize * 0.18)) + 'px',
              height: Math.max(8, Math.floor(cellSize * 0.18)) + 'px',
              borderRadius: '50%',
              backgroundColor: hashStringToColor(String(cellData.groupid)),
              border: '1px solid rgba(0,0,0,0.15)',
              boxShadow: '0 0 0 2px rgba(255,255,255,0.15) inset'
            }}
          />
        )}
        {isSelected && <div className="selection-indicator">✓</div>}
        {isRequired && <div className="required-indicator">!</div>}
        {cellData.status !== 'Available' && !isInvisible && (
          isReservedByMe ? (
            <div className="badge badge-reserved-mine" title="Reserved by you">✓</div>
          ) : isPendingByMe ? (
            <div className="badge badge-pending-mine" title="Pending by you">⏳</div>
          ) : isOnHold ? (
            <div className="badge badge-hold" title="On-Hold">🔒</div>
          ) : isReservedByOther ? (
            <div className="badge badge-reserved-other" title="Reserved by others">✖</div>
          ) : (
            <div className="badge badge-pending-other" title="Pending by others">⏳</div>
          )
        )}
      </div>
    </button>
  );
};

const BoothSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // Determine a back navigation target if provided (e.g., from Layout Builder preview)
  const backTo = (location.state && location.state.backTo) || '/';
  // Keep a reference to the incoming layout so we can send it back to the builder
  const returnLayout = (location.state && (location.state.existingLayout || location.state.layoutData)) || null;

  // Layout state
  const [layoutName, setLayoutName] = useState('');
  const [rows, setRows] = useState(0);
  const [columns, setColumns] = useState(0);
  const [cellSize, setCellSize] = useState(30);
  const [cellPadding, setCellPadding] = useState(5);
  const [fontSize, setFontSize] = useState(12);
  const [customLevels, setCustomLevels] = useState(undefined);
  const [surroundings, setSurroundings] = useState({ top: [], right: [], bottom: [], left: [], corners: {} });
  const [surroundingsBgColor, setSurroundingsBgColor] = useState(null);
  const [surroundingsTextColor, setSurroundingsTextColor] = useState(null);
  const [surroundingsFontSize, setSurroundingsFontSize] = useState(null);
  const [gridData, setGridData] = useState([]);
  const [reservationOverlayMap, setReservationOverlayMap] = useState({});
  const [eventInfo, setEventInfo] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [shoppingCartWaitMinutes, setShoppingCartWaitMinutes] = useState(null);
  const [paymentWaitMinutes, setPaymentWaitMinutes] = useState(null);
  const [layoutId, setLayoutId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [cartItemMap, setCartItemMap] = useState({}); // boothId -> cartItemId
  // Responsive sizing for grid (displayCellSize). Feature flag allows easy rollback.
  const ENABLE_RESPONSIVE_CELL_SIZE = true; // set to false to disable responsive sizing
  const gridWrapperRef = useRef(null);
  const dynamicGridRef = useRef(null);
  const [gridInnerSize, setGridInnerSize] = useState({ width: null, height: null, left: null });

  // Compute a side column width that matches the top/bottom band thickness
  const computedBandFontSize = surroundingsFontSize ? Number(surroundingsFontSize) : 14;
  // vertical padding used in bands is 8px top + 8px bottom = 16
  const bandThickness = computedBandFontSize + 16;
  // ensure a sensible minimum so labels remain readable
  const sideColumnWidth = Math.max(36, Math.round(bandThickness));
  // displayCellSize is the rendered cell size (may be >= configured `cellSize`)
  const [displayCellSize, setDisplayCellSize] = useState(cellSize);

  // Selection state
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [cartActionBusy, setCartActionBusy] = useState(false);
  const cartActionBusyCountRef = useRef(0);

  const beginCartActionBusy = () => {
    cartActionBusyCountRef.current += 1;
    setCartActionBusy(true);
  };

  const endCartActionBusy = () => {
    cartActionBusyCountRef.current = Math.max(0, cartActionBusyCountRef.current - 1);
    if (cartActionBusyCountRef.current === 0) {
      setCartActionBusy(false);
    }
  };

  useEffect(() => {
    const prevCursor = document.body.style.cursor;
    if (cartActionBusy) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = '';
    }
    return () => {
      document.body.style.cursor = prevCursor || '';
    };
  }, [cartActionBusy]);

  const [showBoothRules, setShowBoothRules] = useState(false);
  // Embedded cart replaces popup
  const [ruleViolations, setRuleViolations] = useState([]);
  const [ruleSuggestions, setRuleSuggestions] = useState([]);
  const [requiredBooths, setRequiredBooths] = useState([]);
  const [cartCollapsed, setCartCollapsed] = useState(false);
  // Cart expiry timer
  const [cartExpiryTs, setCartExpiryTs] = useState(null); // epoch ms
  const [timeLeftSeconds, setTimeLeftSeconds] = useState(null);
  const [timeoutModalOpen, setTimeoutModalOpen] = useState(false);
  const [timeoutReleasedBooths, setTimeoutReleasedBooths] = useState([]); // array of { id, number }
  // Track when live reservation overlay has primed at least once
  const [reservationsPrimed, setReservationsPrimed] = useState(false);
  // Track page visibility to pause listeners when not active
  const [isPageVisible, setIsPageVisible] = useState(true);
  // Prevent multiple timeout releases
  const timeoutReleaseInProgress = useRef(false);
  
  // Vendor and clock state
  const [vendorName, setVendorName] = useState('');
  const [vendorId, setVendorId] = useState(null); // Vendor ID for reservation ownership
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  const hasValidShoppingCartWait = Number.isFinite(shoppingCartWaitMinutes) && shoppingCartWaitMinutes > 0;

  // Rules config (derived from layout)
  const [rulesConfig, setRulesConfig] = useState(BOOTH_RULES_CONFIG);

  // Preview Mode flag: either explicitly requested by route or implied by missing event
  const previewRequested = !!(location.state && location.state.preview === true);
  const isPreview = previewRequested || !eventId;

  // Monitor page visibility to reduce Firestore load when tab is inactive
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Warn on full page unload (close/refresh/navigation) when cart has selections
  useEffect(() => {
    const beforeUnload = (e) => {
      if (!isPreview && selectedCells && selectedCells.size > 0) {
        try {
          const uid = currentUser?.uid || 'anon';
          const key = `pendingRelease:${uid}:${eventId}:${layoutId}`;
          try { sessionStorage.setItem(key, JSON.stringify(Array.from(selectedCells))); } catch (err) {}
        } catch (err) {
          // ignore storage failures
        }
        const msg = 'You have selected booths that will be released if you leave this page.';
        e.preventDefault();
        e.returnValue = msg; // Chrome requires returnValue to be set
        return msg;
      }
      return undefined;
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [selectedCells, isPreview]);

  // On load, attempt to release any booths that were marked for pending release
  useEffect(() => {
    let cancelled = false;
    const tryReleasePending = async () => {
      // Require vendorId as ownership is checked server-side; skip until vendorId is known
      if (!currentUser?.uid || !eventId || !layoutId || !vendorId) return;
      const key = `pendingRelease:${currentUser.uid}:${eventId}:${layoutId}`;
      try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return;
        const ids = JSON.parse(raw);
        if (!Array.isArray(ids) || ids.length === 0) {
          try { sessionStorage.removeItem(key); } catch (e) {}
          return;
        }
        for (const id of ids) {
          if (cancelled) break;
          let actuallyReleased = [];
          try {
            await releaseBoothRemote({ eventId, layoutId, boothId: id, vendorId, createdByProfileId: currentUser?.uid, sessionId, triggeredBy: 'pending-release-recovery' });
            // Update local gridData if present
            setGridData(prev => prev.map(c => c.id === id ? { ...c, status: 'Available', reservedByMe: false, pendingByMe: false } : c));
          } catch (err) {
            console.error('[PendingRelease] Failed to release booth', id, err);
          }
        }
      } catch (err) {
        console.error('[PendingRelease] Error processing pending releases', err);
      } finally {
        try { sessionStorage.removeItem(key); } catch (e) {}
      }
    };
    tryReleasePending();
    return () => { cancelled = true; };
  }, [currentUser?.uid, eventId, layoutId, vendorId]);

  // Load vendor name and ID
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.uid) return;
      try {
        // First try to get vendor name and ID from vendors collection
        const vendors = await listVendorsForUser(currentUser.uid);
        if (!cancelled && vendors && vendors.length > 0) {
          setVendorName(vendors[0].name || '');
          setVendorId(vendors[0].id || null);
        } else {
          // Fall back to profile business name for Organizers/Admins without vendor records
          try {
            const profile = await getUserProfile(currentUser.uid);
            if (!cancelled && profile?.businessName) {
              setVendorName(profile.businessName);
            }
            // Get vendorId from profile
            if (!cancelled && profile?.vendorId) {
              setVendorId(profile.vendorId);
            }
          } catch (err) {
            console.error('[BoothSelection] Error loading profile', err);
          }
        }
      } catch (err) {
        console.error('[BoothSelection] Error loading vendor', err);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load layout from navigation state
  useEffect(() => {
    const incoming = location.state && (location.state.existingLayout || location.state.layoutData);
    if (!incoming) {
      // If no layout provided, send back home
      navigate('/', { replace: true });
      return;
    }

    setLayoutName(incoming.name || 'Booth Selection');
    const { gridSettings, cellData, customLevels: layoutLevels, rules, boothGroups } = incoming;
    setLayoutId(incoming.id || null);
    const incomingEvent = location.state && location.state.event;
    if (incomingEvent) {
      setEventId(incomingEvent.id || null);
      const wait = parsePositiveMinutes(incomingEvent.shoppingCartWait);
      setShoppingCartWaitMinutes(wait);
      const paymentWait = parsePositiveMinutes(incomingEvent.paymentWait);
      setPaymentWaitMinutes(paymentWait);
      const fmt = (d) => {
        try {
          const dt = typeof d === 'string' ? new Date(d) : d;
          if (!dt || isNaN(dt.getTime())) return null;
          return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        } catch {
          return d || null;
        }
      };
      const s = fmt(incomingEvent.startDate);
      const e = fmt(incomingEvent.endDate);
      const dates = s && e ? (s === e ? s : `${s} - ${e}`) : (s || e || '');
      setEventInfo({ name: incomingEvent.name || '', dates });
    } else {
      setEventInfo(null);
    }
    if (gridSettings) {
      setRows(gridSettings.rows);
      setColumns(gridSettings.columns);
      setCellSize(gridSettings.cellSize);
      setCellPadding(gridSettings.cellPadding);
      setFontSize(gridSettings.fontSize);
      try { setSurroundings(gridSettings.surroundings || { top: [], right: [], bottom: [], left: [], corners: {} }); } catch (e) {}
      setSurroundingsBgColor(gridSettings.surroundingsBgColor || null);
      setSurroundingsTextColor(gridSettings.surroundingsTextColor || null);
      setSurroundingsFontSize(gridSettings.surroundingsFontSize || null);
    }
    setCustomLevels(layoutLevels || undefined);
    // Normalize cellData so saved overrides (manualCorner/manualNumber) are applied
    const incomingCells = Array.isArray(cellData) ? cellData : (cellData && typeof cellData === 'object' ? Object.values(cellData) : []);
    const normalizedCells = incomingCells.map(c => {
      const cell = { ...c };
      cell.cornerOverride = !!cell.cornerOverride;
      cell.numberOverride = !!cell.numberOverride;
      if (cell.cornerOverride && (cell.manualCorner === 'Y' || cell.manualCorner === 'N')) {
        cell.corner = cell.manualCorner;
      }
      return cell;
    });
    setGridData(normalizedCells);

    // Build rulesConfig from persisted rules + groups
    const baseConfig = { ...BOOTH_RULES_CONFIG };

    // Helper to resolve a config key from either a key (e.g., 'CORNER_ADJACENCY') or rule.id (e.g., 'corner-adjacency')
    const resolveRuleKey = (idOrKey) => {
      if (!idOrKey) return undefined;
      if (baseConfig[idOrKey]) return idOrKey;
      const match = Object.entries(baseConfig).find(([, rule]) => rule.id === idOrKey);
      return match ? match[0] : undefined;
    };

    const config = { ...baseConfig };

    // Disable all rules by default. Only enable rules when the layout provides
    // a non-empty `rules` array. This removes legacy default behavior.
    Object.keys(config).forEach(key => { config[key].enabled = false; config[key].groups = undefined; });
    if (Array.isArray(rules) && rules.length > 0) {
      rules.forEach(({ ruleId, parameters }) => {
        const key = resolveRuleKey(ruleId);
        if (!key) return;
        config[key].enabled = true;
        config[key].parameters = parameters;
        if (parameters && typeof parameters === 'object') {
          Object.entries(parameters).forEach(([k, v]) => {
            if (k === 'groups') return;
            if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') {
              config[key][k] = v;
            }
          });
        }
      });
    }

    // Apply booth groups if present (map by either key or rule.id)
    if (boothGroups && typeof boothGroups === 'object') {
      Object.entries(boothGroups).forEach(([ruleKeyOrId, groups]) => {
        const key = resolveRuleKey(ruleKeyOrId);
        if (!key || !config[key]) return;
        config[key].groups = Array.isArray(groups) ? groups : undefined;
      });
    }

    setRulesConfig(config);
  }, [location.state, navigate]);

  // Compute displayCellSize so grid uses real layout sizing (never shrink below configured cellSize)
  useEffect(() => {
    if (!ENABLE_RESPONSIVE_CELL_SIZE) {
      setDisplayCellSize(cellSize);
      return;
    }
    let mounted = true;
    const compute = () => {
      const wrapper = gridWrapperRef.current;
      if (!wrapper) return;
      const availW = wrapper.clientWidth || wrapper.getBoundingClientRect().width;
      const availH = wrapper.clientHeight || wrapper.getBoundingClientRect().height;
      const totalGapW = Math.max(0, columns - 1) * cellPadding;
      const totalGapH = Math.max(0, rows - 1) * cellPadding;
      const candidateW = Math.floor((availW - totalGapW) / columns);
      const candidateH = Math.floor((availH - totalGapH) / rows);
      // Prefer width so layouts expand horizontally and allow vertical scrolling.
      let candidate = null;
      if (candidateW > 0) candidate = candidateW;
      else if (candidateH > 0) candidate = candidateH;
      if (candidate == null || !isFinite(candidate)) {
        if (mounted) setDisplayCellSize(cellSize);
        return;
      }
      const maxScale = 1.2; // allow enlarging up to 1.2x
      const maxAllowed = Math.floor(cellSize * maxScale);
      const finalSize = Math.max(cellSize, Math.min(candidate, maxAllowed));
      if (mounted) setDisplayCellSize(finalSize);
    };

    compute();
    const onResize = () => compute();
    window.addEventListener('resize', onResize);
    let ro = null;
    try {
      ro = new ResizeObserver(() => compute());
      if (gridWrapperRef.current) ro.observe(gridWrapperRef.current);
    } catch (e) {}
    return () => { mounted = false; window.removeEventListener('resize', onResize); if (ro && gridWrapperRef.current) ro.unobserve(gridWrapperRef.current); };
  }, [columns, rows, cellSize, cellPadding]);

  // Track the dynamic grid's rendered size so surrounding labels can match width/height
  useEffect(() => {
    let mounted = true;
    const updateGridInnerSize = () => {
      try {
        const el = dynamicGridRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        // Compute left offset relative to the wrapper so labels can align
        const wrapper = gridWrapperRef.current;
        let left = null;
        if (wrapper) {
          try {
            const wr = wrapper.getBoundingClientRect();
            left = Math.round(r.left - wr.left);
          } catch (e) { left = null; }
        }
        if (mounted) setGridInnerSize({ width: Math.round(r.width), height: Math.round(r.height), left });
      } catch (e) {}
    };

    updateGridInnerSize();
    const ro = (typeof ResizeObserver !== 'undefined') ? new ResizeObserver(updateGridInnerSize) : null;
    if (ro && dynamicGridRef.current) ro.observe(dynamicGridRef.current);
    window.addEventListener('resize', updateGridInnerSize);
    return () => { mounted = false; window.removeEventListener('resize', updateGridInnerSize); if (ro && dynamicGridRef.current) ro.unobserve(dynamicGridRef.current); };
  }, [columns, rows, displayCellSize, cellPadding, gridData.length]);

  const expiryStorageKey = (uid, evId, lyId, sid) => uid && evId && lyId && sid ? `cartExpiry:${uid}:${evId}:${lyId}:${sid}` : null;

  // Reflect live reservations (Pending/Reserved) in the grid via realtime subscription
  useEffect(() => {
    if (isPreview) return; // no live overlays in Preview Mode
    if (!isPageVisible) return; // pause listeners when tab is not visible
    if (!eventId || !layoutId || !Array.isArray(gridData) || gridData.length === 0) return;
    // Prime once with current snapshot in case there are existing reservations
    let unsubscribe = subscribeReservationsForLayout({ eventId, layoutId, onChange: (map) => {
      // Store overlay map for periodic re-evaluation (expirations)
      try { setReservationOverlayMap(map || {}); } catch (e) {}
      // Apply statuses from reservation map
      setGridData(prev => prev.map(cell => {
        // Do not overlay availability on invisible cells
        if (cell.status === 'Invisible' || cell.level === 'Invisible') {
          return cell;
        }
        const info = map[cell.id];
        // If we have a reservation entry, decide if it's still valid
        if (info && (info.status === 'Pending' || info.status === 'Reserved' || info.status === 'On-Hold')) {
          if (info.status === 'Reserved') return { ...cell, status: 'Reserved', reservedByMe: info.vendorId === vendorId };
          if (info.status === 'On-Hold') {
            // If an optional expiresAt exists and is past, treat as available
            const toMillis = (ts) => {
              try {
                if (!ts) return null;
                if (typeof ts.toMillis === 'function') return ts.toMillis();
                if (ts.seconds) return ts.seconds * 1000;
                const d = new Date(ts);
                return isNaN(d.getTime()) ? null : d.getTime();
              } catch { return null; }
            };
            const expiresMs = toMillis(info.expiresAt);
            const expired = typeof expiresMs === 'number' && expiresMs <= Date.now();
            if (!expired) return { ...cell, status: 'On-Hold' };
          }
          // For Pending, ignore if expired according to createdAt/expiresAt and event wait
          const toMillis = (ts) => {
            try {
              if (!ts) return null;
              if (typeof ts.toMillis === 'function') return ts.toMillis();
              if (ts.seconds) return ts.seconds * 1000;
              const d = new Date(ts);
              return isNaN(d.getTime()) ? null : d.getTime();
            } catch { return null; }
          };
          const createdMs = toMillis(info.createdAt);
          const expiresMsExplicit = toMillis(info.expiresAt);
          const expiresMs = typeof expiresMsExplicit === 'number'
            ? expiresMsExplicit
            : (hasValidShoppingCartWait && createdMs ? createdMs + shoppingCartWaitMinutes * 60 * 1000 : null);
          const expired = typeof expiresMs === 'number' && expiresMs <= Date.now();
          if (!expired) return { ...cell, status: 'Pending', pendingByMe: info.vendorId === vendorId };
          // expired -> fall through to potentially revert
        }
        // If not in the map or expired and previous status was Pending/Reserved/On-Hold, revert to Available
        if (prev && (cell.status === 'Pending' || cell.status === 'Reserved' || cell.status === 'On-Hold') && !info) {
          return { ...cell, status: 'Available', reservedByMe: false, pendingByMe: false };
        }
        if (prev && cell.status === 'Pending' && info && info.status === 'Pending') {
          // expired pending case already handled above -> revert to Available
          const toMillis = (ts) => {
            try {
              if (!ts) return null;
              if (typeof ts.toMillis === 'function') return ts.toMillis();
              if (ts.seconds) return ts.seconds * 1000;
              const d = new Date(ts);
              return isNaN(d.getTime()) ? null : d.getTime();
            } catch { return null; }
          };
          const createdMs = toMillis(info.createdAt);
          const expiresMsExplicit = toMillis(info.expiresAt);
          const expiresMs = typeof expiresMsExplicit === 'number'
            ? expiresMsExplicit
            : (hasValidShoppingCartWait && createdMs ? createdMs + shoppingCartWaitMinutes * 60 * 1000 : null);
          const expired = typeof expiresMs === 'number' && expiresMs <= Date.now();
          if (expired) return { ...cell, status: 'Available', reservedByMe: false, pendingByMe: false };
        }
        // Also handle expired On-Hold when a doc still exists but its expiresAt is past
        if (prev && cell.status === 'On-Hold' && info && info.status === 'On-Hold') {
          const toMillis = (ts) => {
            try {
              if (!ts) return null;
              if (typeof ts.toMillis === 'function') return ts.toMillis();
              if (ts.seconds) return ts.seconds * 1000;
              const d = new Date(ts);
              return isNaN(d.getTime()) ? null : d.getTime();
            } catch { return null; }
          };
          const expiresMs = toMillis(info.expiresAt);
          const expired = typeof expiresMs === 'number' && expiresMs <= Date.now();
          if (expired) return { ...cell, status: 'Available', reservedByMe: false, pendingByMe: false };
        }
        return cell;
      }));
      // Mark that we've applied at least one live overlay update
      setReservationsPrimed(true);
    }});
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [isPreview, isPageVisible, eventId, layoutId, gridData.length, shoppingCartWaitMinutes, hasValidShoppingCartWait, currentUser?.uid, vendorId]);

  // Periodically re-evaluate overlay expirations using the reservationOverlayMap so
  // clients reflect expirations in real-time even if the server doc isn't updated.
  useEffect(() => {
    if (!reservationOverlayMap || Object.keys(reservationOverlayMap).length === 0) return;
    if (!reservationsPrimed || !Array.isArray(gridData) || gridData.length === 0) return;

    // Use a fixed 5 second interval to limit CPU work while keeping expirations timely
    const intervalMs = 5000;

    const toMillis = (ts) => {
      try {
        if (!ts) return null;
        if (typeof ts.toMillis === 'function') return ts.toMillis();
        if (ts.seconds) return ts.seconds * 1000;
        const d = new Date(ts);
        return isNaN(d.getTime()) ? null : d.getTime();
      } catch { return null; }
    };

    const recompute = () => {
      setGridData(prev => prev.map(cell => {
        if (cell.status === 'Invisible' || cell.level === 'Invisible') return cell;
        const info = reservationOverlayMap[cell.id];
        if (info && (info.status === 'Pending' || info.status === 'On-Hold' || info.status === 'Reserved')) {
          // For Pending: compute expiry from expiresAt or createdAt + wait
          if (info.status === 'Reserved') return { ...cell, status: 'Reserved', reservedByMe: info.vendorId === vendorId };
          if (info.status === 'On-Hold') {
            const expiresMs = toMillis(info.expiresAt);
            const expired = typeof expiresMs === 'number' && expiresMs <= Date.now();
            if (!expired) return { ...cell, status: 'On-Hold' };
            return { ...cell, status: 'Available', reservedByMe: false, pendingByMe: false };
          }
          const createdMs = toMillis(info.createdAt);
          const expiresMsExplicit = toMillis(info.expiresAt);
          const expiresMs = typeof expiresMsExplicit === 'number'
            ? expiresMsExplicit
            : (hasValidShoppingCartWait && createdMs ? createdMs + shoppingCartWaitMinutes * 60 * 1000 : null);
          const expired = typeof expiresMs === 'number' && expiresMs <= Date.now();
          if (!expired) return { ...cell, status: 'Pending', pendingByMe: info.vendorId === vendorId };
          return { ...cell, status: 'Available', reservedByMe: false, pendingByMe: false };
        }
        // If no overlay info but cell was Pending/Reserved/On-Hold, revert
        if (cell.status === 'Pending' || cell.status === 'Reserved' || cell.status === 'On-Hold') {
          return { ...cell, status: 'Available', reservedByMe: false, pendingByMe: false };
        }
        return cell;
      }));
    };

    // run immediately then at interval
    recompute();
    const id = setInterval(recompute, intervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationOverlayMap, reservationsPrimed, shoppingCartWaitMinutes, hasValidShoppingCartWait, gridData.length, vendorId]);

  // Establish a sessionId to group cart activity for this user/event/layout within the browser tab
  useEffect(() => {
    if (!currentUser?.uid || !eventId || !layoutId) return;
    const key = `cartSession:${currentUser.uid}:${eventId}:${layoutId}`;
    const makeUuid = () => {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
      // Fallback UUID v4 generator
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    };
    try {
      let sid = sessionStorage.getItem(key);
      if (!sid) {
        sid = makeUuid();
        sessionStorage.setItem(key, sid);
      }
      setSessionId(sid);
    } catch (e) {
      // sessionStorage might be blocked; still assign an in-memory id
      setSessionId(makeUuid());
    }
  }, [currentUser?.uid, eventId, layoutId]);

  // Load/save cartItem map in sessionStorage so we can update the same record on remove/paid
  useEffect(() => {
    if (!currentUser?.uid || !eventId || !layoutId) return;
    const key = `cartItemMap:${currentUser.uid}:${eventId}:${layoutId}`;
    try {
      const raw = sessionStorage.getItem(key);
      setCartItemMap(raw ? JSON.parse(raw) : {});
    } catch {
      setCartItemMap({});
    }
    return () => {
      // persist on unmount/navigation as well
      try { sessionStorage.setItem(key, JSON.stringify(cartItemMap)); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, eventId, layoutId]);

  const persistCartItemMap = (next) => {
    setCartItemMap(next);
    if (!currentUser?.uid || !eventId || !layoutId) return;
    const key = `cartItemMap:${currentUser.uid}:${eventId}:${layoutId}`;
    try { sessionStorage.setItem(key, JSON.stringify(next)); } catch {}
  };

  // On refresh or first load, hydrate cart from cartItems for this session
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.uid || !eventId || !layoutId || !sessionId) return;
      try {
        // Defer claiming orphans until reservations overlay is primed to avoid false negatives
        const items = await loadActiveCartItems({
          profileId: currentUser.uid,
          eventId,
          layoutId,
          sessionId,
        });
        if (cancelled) return;
        const nextMap = {};
        const boothIds = [];
        for (const it of items) {
          const cid = it.cartItemId || it.id;
          if (it.boothId && cid) {
            nextMap[it.boothId] = cid;
            boothIds.push(it.boothId);
          }
        }
        // Important: don't treat items as stale until reservations overlay is primed,
        // otherwise we can misclassify and incorrectly remove/log on refresh.
        let activeBoothIds = boothIds;
        if (reservationsPrimed) {
          const staleBoothIds = [];
          for (const id of boothIds) {
            const cell = gridData.find(c => c.id === id);
            // Missing cell -> consider stale
            if (!cell) { staleBoothIds.push(id); continue; }
            // Prefer authoritative overlay info when available
            const overlay = reservationOverlayMap && reservationOverlayMap[id];
            if (overlay) {
              const st = overlay.status || '';
              // If overlay shows Pending/Reserved held by someone else, mark stale
              if ((st === 'Pending' || st === 'Reserved') && overlay.vendorId && overlay.vendorId !== vendorId) {
                staleBoothIds.push(id);
                continue;
              }
              // If overlay shows On-Hold, treat as stale (admin hold)
              if (st === 'On-Hold') { staleBoothIds.push(id); continue; }
              // Otherwise overlay indicates it's ours or available -> keep it
              continue;
            }
            // No overlay info: be conservative and use local cell flags only to remove when clearly not ours
            if (cell.status === 'Available') {
              // If we have a cart mapping for this booth, prefer keeping it (maybe overlay not loaded for non-admins)
              if (!nextMap[id]) {
                staleBoothIds.push(id);
              }
              continue;
            }
            if (cell.status === 'Pending' && !cell.pendingByMe) { staleBoothIds.push(id); continue; }
            if (cell.status === 'Reserved' && !cell.reservedByMe) { staleBoothIds.push(id); continue; }
          }

          if (staleBoothIds.length > 0) {
            for (const id of staleBoothIds) {
              const mapped = nextMap[id];
              if (mapped) {
                try {
                  await updateCartItemAction(mapped, 'removed', {
                    triggeredByProfileIdOverride: currentUser?.uid,
                    triggeredByOverride: 'stale-booth-cleanup'
                  });
                } catch {}
                delete nextMap[id];
              }
            }
          }
          activeBoothIds = boothIds.filter(id => !staleBoothIds.includes(id));
        }
        persistCartItemMap(nextMap);
        if (activeBoothIds.length > 0) {
          const set = new Set(activeBoothIds);
          setSelectedCells(set);
          validateBoothSelection(activeBoothIds);
        } else {
          setSelectedCells(new Set());
          setCartExpiryTs(null);
          setTimeLeftSeconds(null);
        }
      } catch (e) {
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid, eventId, layoutId, sessionId, shoppingCartWaitMinutes, reservationsPrimed]);

  // After reservations primed, claim any of my orphan reservations to this session,
  // then reconcile any selected cells that no longer have a live Pending/Reserved status
  // After reservations primed, claim any of my orphan reservations to this session,
  // then reconcile any selected cells that no longer have a live Pending/Reserved status
  const reconciliationRunning = useRef(false);
  
  // Create a dependency value that changes when selected booths' status changes
  const selectedBoothsStatus = useMemo(() => {
    return Array.from(selectedCells).map(id => {
      const cell = gridData.find(c => c.id === id);
      return cell ? `${id}:${cell.status}:${cell.pendingByMe}:${cell.reservedByMe}` : `${id}:missing`;
    }).join('|');
  }, [selectedCells, gridData]);
  
  useEffect(() => {
    if (!reservationsPrimed) return;
    if (selectedCells.size === 0) return;
    if (reconciliationRunning.current) return; // Prevent concurrent runs
    if (timeoutReleaseInProgress.current) return; // Don't reconcile during timeout release
    
    reconciliationRunning.current = true;
    
    // Immediately check and handle removed booths synchronously
    const currentIds = Array.from(selectedCells);
    const stillHeld = currentIds.filter(id => {
      const cell = gridData.find(c => c.id === id);
      if (!cell) return false;
      if (cell.status === 'Pending') return !!cell.pendingByMe;
      if (cell.status === 'Reserved') return !!cell.reservedByMe;
      // If booth is Available, only keep it if there's no cart mapping (freshly clicked)
      // If there IS a cart mapping but status is Available, it means we lost it
      if (cell.status === 'Available' && !cartItemMap[id]) return true;
      return false;
    });
    if (stillHeld.length !== currentIds.length) {
      const nextSet = new Set(stillHeld);
      setSelectedCells(nextSet);
      validateBoothSelection(stillHeld);
      // Purge stale mapping entries and mark removed in cartItems
      (async () => {
        const nextMap = { ...cartItemMap };
        const toRemove = currentIds.filter(id => !stillHeld.includes(id));
        for (const id of toRemove) {
          const mapped = nextMap[id];
          if (mapped) {
            try { 
              await updateCartItemAction(mapped, 'removed', {
                triggeredByProfileIdOverride: currentUser?.uid,
                triggeredByOverride: 'booth-lost-to-another-user'
              }); 
            } catch (err) {
              console.error('[Reconciliation] Failed to update cart item', mapped, err);
            }
            delete nextMap[id];
          }
        }
        persistCartItemMap(nextMap);
      })();
    }
    
    // Clear the reconciliation lock NOW so the effect can run again if needed
    reconciliationRunning.current = false;
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationsPrimed, selectedCells.size, selectedBoothsStatus]); // Run when selection size OR status of selected booths changes

  const validateBoothSelection = (selectedBoothIds) => {
    const validation = validateBoothRules(selectedBoothIds, gridData, rulesConfig);
    setRuleViolations(validation.violations);
    setRuleSuggestions(validation.suggestions);
    setRequiredBooths(validation.requiredBooths);
    if (validation.violations.length > 0 || validation.suggestions.length > 0) {
      setShowBoothRules(true);
    }
    return validation;
  };

  const handleCellClick = async (cellData) => {
    // Block interactions until live availability overlay has primed (except in Preview Mode)
    if (!isPreview && !reservationsPrimed) return;
    if (cellData.status !== 'Available') return;
    
    // CRITICAL: Check if cart timer has expired before allowing any booth selection
    if (!isPreview && selectedCells.size > 0 && cartExpiryTs) {
      const now = Date.now();
      if (now >= cartExpiryTs) {
        alert('Your cart time has expired. Your selections have been released. Please start over.');
        // The timer effect will handle the release, just prevent this selection
        return;
      }
    }

    if (!isPreview && !hasValidShoppingCartWait) {
      alert('Booth reservation is temporarily unavailable: shopping cart timer configuration is missing or invalid.');
      return;
    }
    
    const next = new Set(selectedCells);
    const prevSelectedSet = new Set(selectedCells);
    const alreadySelected = next.has(cellData.id);
    const wasEmptyBefore = selectedCells.size === 0;
    if (alreadySelected) next.delete(cellData.id); else next.add(cellData.id);
    // Defer updating `selectedCells` for additions until reserve succeeds to avoid
    // showing a cart item that wasn't actually reserved. For removals we update immediately below.

    // Using cartItems only (no immutable audit log)

    // Maintain reservation + cartItems document per add/remove lifecycle
    let busyStarted = false;
    try {
      const canTransact = !isPreview && currentUser?.uid && vendorId && eventId && layoutId && sessionId && hasValidShoppingCartWait;
      if (!canTransact) {
        // Skipping reserve/release: missing context
        if (!vendorId && !isPreview && currentUser?.uid) {
          console.warn('[BoothSelection] Cannot add booth to cart - vendorId not found. User may not have a vendor profile.');
          alert('Unable to reserve booth. Please ensure you have a vendor profile set up. Contact support if this issue persists.');
          return;
        }
      }
      if (canTransact) {
  beginCartActionBusy();
  busyStarted = true;
  const booth = cellData;
  // getLevelPricing now returns cents, so use as-is
  const price = (typeof getLevelPricing(booth.level, booth.corner === 'Y', customLevels) === 'number' && !isNaN(getLevelPricing(booth.level, booth.corner === 'Y', customLevels))) ? getLevelPricing(booth.level, booth.corner === 'Y', customLevels) : null;
        if (!alreadySelected) {
          // Added: first reserve the booth (server-side check for availability)
          let reservedOk = false;
          try {
            const effectiveExpiresAtMs = (cartExpiryTs && !wasEmptyBefore)
                ? cartExpiryTs
                : Date.now() + shoppingCartWaitMinutes * 60 * 1000;
              await reserveBoothRemote({ 
              eventId, 
              layoutId, 
              boothId: booth.id, 
              vendorId, 
              createdByProfileId: currentUser.uid, 
              sessionId, 
              triggeredBy: 'cart',
              shoppingCartWaitMinutes, 
              price,
              boothNumber: booth.number || booth.boothNumber,
              level: booth.level,
              corner: booth.corner,
              row: booth.row,
              col: booth.col,
              expiresAt: effectiveExpiresAtMs,
            });
            reservedOk = true;
          } catch (err) {
            const code = err?.code || '';
            console.error('[BoothSelection] reserveBoothRemote failed', { boothId: booth.id, code, error: err?.message || err });
            // Revert optimistic selection when backend reserve fails and show a specific message when possible.
            try { setSelectedCells(prevSelectedSet); validateBoothSelection(Array.from(prevSelectedSet)); } catch (e) {}
            const message = String(err?.message || '').trim();
            const isAlreadyReserved = code === 'already-reserved' || code === 'failed-precondition' || /already\s*reserved|no\s*longer\s*available|taken/i.test(message);
            if (isAlreadyReserved) {
              alert('Sorry, that booth was just reserved by someone else. Please pick another one.');
            } else if (message) {
              alert(`Unable to reserve booth: ${message}`);
            } else {
              alert('Unable to reserve booth right now. Please try again.');
            }
            return; // don't proceed
          }

          if (!reservedOk) {
            // Safety: do not proceed if reservation/claim did not succeed - revert optimistic selection
            try { setSelectedCells(prevSelectedSet); validateBoothSelection(Array.from(prevSelectedSet)); } catch (e) {}
            return;
          }

          // Locally mark as Pending so UI locks it immediately
          setGridData(prev => prev.map(c => c.id === booth.id ? { ...c, status: 'Pending', pendingByMe: true } : c));

          // Create a new cart item document and store mapping
          const cartItemId = await createCartItem({
            cartItemId: undefined,
            eventId,
            layoutId,
            profileId: currentUser.uid,
            sessionId,
            booth,
            price,
          });
          persistCartItemMap({ ...cartItemMap, [booth.id]: cartItemId });

          // Now that reservation and cart creation succeeded, update visible selection
          try { setSelectedCells(next); validateBoothSelection(Array.from(next)); } catch (e) {}
        } else {
          // For removals, update selection immediately for snappy UX
          try { setSelectedCells(next); validateBoothSelection(Array.from(next)); } catch (e) {}

          // Removed: release reservation then update cart item if mapping exists
          try {
            await releaseBoothRemote({ eventId, layoutId, boothId: booth.id, vendorId, createdByProfileId: currentUser?.uid, sessionId, triggeredBy: 'Cart', type: 'Removed' });
            // Mark locally as Available again and clear flags
            setGridData(prev => prev.map(c => c.id === booth.id ? { ...c, status: 'Available', reservedByMe: false, pendingByMe: false } : c));
          } catch (err) {
            console.error('[BoothSelection] releaseBoothRemote failed', { boothId: booth.id, error: err?.message || err });
          }
          const cartItemId = cartItemMap[booth.id];
          if (cartItemId) {
            await updateCartItemAction(cartItemId, 'removed', {
              triggeredByProfileIdOverride: currentUser.uid,
              triggeredByOverride: 'booth-deselected'
            });
            const nextMap = { ...cartItemMap };
            delete nextMap[booth.id];
            persistCartItemMap(nextMap);
          }
        }
      }
    } catch (e) {
      console.error('[BoothSelection] Unexpected error in handleCellClick', e);
    } finally {
      if (busyStarted) endCartActionBusy();
    }
  };

  const handleApplySuggestions = async (suggestions) => {
    const currentSelection = Array.from(selectedCells);
    let newSelection = applyAutoSuggestions(currentSelection, suggestions, gridData);
    const additions = newSelection.filter(id => !selectedCells.has(id));
    const removals = currentSelection.filter(id => !newSelection.includes(id));

    // If we have necessary context, perform reservation/release like manual clicks
  const canTransact = !!(!isPreview && currentUser?.uid && vendorId && eventId && layoutId && sessionId && hasValidShoppingCartWait);

    // Handle additions (reserve + cart item)
    if (canTransact && additions.length > 0) {
      for (const boothId of additions) {
        const booth = gridData.find(c => c.id === boothId);
        if (!booth) continue;
        const price = getLevelPricing(booth.level, booth.corner === 'Y', customLevels);
        // Attempt to reserve on the server first to actually lock it
        try {
          const effectiveExpiresAtMs = (cartExpiryTs && selectedCells.size > 0)
            ? cartExpiryTs
            : Date.now() + shoppingCartWaitMinutes * 60 * 1000;
          await reserveBoothRemote({ 
            eventId, 
            layoutId, 
            boothId, 
            vendorId, 
            createdByProfileId: currentUser.uid, 
            sessionId, 
            triggeredBy: 'rule-auto-add',
            shoppingCartWaitMinutes, 
            price,
            boothNumber: booth.number || booth.boothNumber,
            level: booth.level,
            corner: booth.corner,
            row: booth.row,
            col: booth.col,
            expiresAt: effectiveExpiresAtMs,
          });
          // Locally mark as Pending so UI locks it immediately
          setGridData(prev => prev.map(c => c.id === boothId ? { ...c, status: 'Pending', pendingByMe: true } : c));
          // Create cart item and map it
          const cartItemId = await createCartItem({
            cartItemId: undefined,
            eventId,
            layoutId,
            profileId: currentUser.uid,
            sessionId,
            booth,
            price,
          });
          persistCartItemMap({ ...cartItemMap, [boothId]: cartItemId });
        } catch (err) {
          // Remove from intended selection if reservation failed
          newSelection = newSelection.filter(id => id !== boothId);
        }
      }
    }

    // Handle removals (release + cart item removed)
    if (canTransact && removals.length > 0) {
      const nextMap = { ...cartItemMap };
      for (const boothId of removals) {
        try {
          await releaseBoothRemote({ eventId, layoutId, boothId, vendorId, createdByProfileId: currentUser?.uid, sessionId, triggeredBy: 'rule-violation-removal' });
          setGridData(prev => prev.map(c => c.id === boothId ? { ...c, status: 'Available', reservedByMe: false, pendingByMe: false } : c));
        } catch (err) {
        }
        const mappedId = nextMap[boothId];
        if (mappedId) {
          try { 
            await updateCartItemAction(mappedId, 'removed', {
              triggeredByProfileIdOverride: currentUser.uid,
              triggeredByOverride: 'rule-violation-removal'
            }); 
          } catch {}
          delete nextMap[boothId];
        }
      }
      persistCartItemMap(nextMap);
    }

    // Reflect final selection and re-validate rules/UI
    setSelectedCells(new Set(newSelection));
    validateBoothSelection(newSelection);
  };

  const handleRemoveFromCart = async (boothId) => {
    const next = new Set(selectedCells);
    next.delete(boothId);
    setSelectedCells(next);
    validateBoothSelection(Array.from(next));

    // Using cartItems only (no immutable audit log)

    // Release reservation then update cartItems doc to removed
    let busyStarted = false;
    try {
      if (currentUser?.uid && vendorId && eventId && layoutId && sessionId) {
        beginCartActionBusy();
        busyStarted = true;
        const booth = gridData.find(b => b.id === boothId);
        if (booth) {
          try {
            await releaseBoothRemote({ eventId, layoutId, boothId, vendorId, createdByProfileId: currentUser?.uid, sessionId, triggeredBy: 'Cart', type: 'Removed' });
            setGridData(prev => prev.map(c => c.id === boothId ? { ...c, status: 'Available', reservedByMe: false, pendingByMe: false } : c));
          } catch (err) {
          }
          const cartItemId = cartItemMap[booth.id];
          if (cartItemId) {
          await updateCartItemAction(cartItemId, 'removed');
          const nextMap = { ...cartItemMap };
          delete nextMap[boothId];
          persistCartItemMap(nextMap);
          }
        }
      }
    } catch (e) {
    } finally {
      if (busyStarted) endCartActionBusy();
    }
  };

  const handleClearCart = async () => {
    const current = Array.from(selectedCells);
    setSelectedCells(new Set());
    setShowBoothRules(false);
    setRuleViolations([]);
    setRuleSuggestions([]);
    setRequiredBooths([]);
    // Clear expiry timer state and storage
    try {
      const key = expiryStorageKey(currentUser?.uid, eventId, layoutId, sessionId);
      if (key) sessionStorage.removeItem(key);
    } catch {}
    setCartExpiryTs(null);
    setTimeLeftSeconds(null);

    // Using cartItems only (no immutable audit log)

    // Release reservations then update cartItems docs to removed and clear mapping
    let busyStarted = false;
    try {
      if (currentUser?.uid && vendorId && eventId && layoutId && sessionId && current.length > 0) {
        beginCartActionBusy();
        busyStarted = true;
        const nextMap = { ...cartItemMap };
        for (const id of current) {
          try {
            await releaseBoothRemote({ eventId, layoutId, boothId: id, vendorId, createdByProfileId: currentUser?.uid, sessionId, triggeredBy: 'Cart', type: 'Removed' });
            setGridData(prev => prev.map(c => c.id === id ? { ...c, status: 'Available', reservedByMe: false, pendingByMe: false } : c));
          } catch (err) {
          }
          const cartItemId = nextMap[id];
          if (cartItemId) {
            await updateCartItemAction(cartItemId, 'removed', {
              triggeredByProfileIdOverride: currentUser.uid,
              triggeredByOverride: 'manual-cart-clear'
            });
            delete nextMap[id];
          }
        }
        persistCartItemMap(nextMap);
      }
    } catch (e) {
    } finally {
      if (busyStarted) endCartActionBusy();
    }
  };

  // Warn and clear cart when navigating away via the Back button
  const handleBack = async () => {
    // If there are selected booths, warn user that leaving will release them
    if (!isPreview && selectedCells.size > 0) {
      const proceed = window.confirm('You have selected booths in your cart. If you navigate away, your selections will be released. Do you want to continue and release them?');
      if (!proceed) return;
      try {
        await handleClearCart();
      } catch (e) {
        console.error('[BoothSelection] Failed to clear cart before navigating:', e);
      }
    }

    if (backTo === '/layout-builder' && returnLayout) {
      navigate('/layout-builder', { state: { existingLayout: returnLayout } });
    } else {
      navigate(backTo);
    }
  };

  // Timer tick: compute remaining time and auto-release on timeout
  useEffect(() => {
    if (!cartExpiryTs) return;
    let cancelled = false;
    let hasTriggeredRelease = false;
    const tick = () => {
      if (cancelled) return;
      const now = Date.now();
      const remainingMs = cartExpiryTs - now;
      const secs = Math.max(0, Math.floor(remainingMs / 1000));
      setTimeLeftSeconds(secs);
      if (remainingMs <= 0 && !hasTriggeredRelease && !timeoutReleaseInProgress.current) {
        // Timeout reached: release all
        hasTriggeredRelease = true;
        (async () => {
          if (timeoutReleaseInProgress.current) return;
          timeoutReleaseInProgress.current = true;
          
          let releasedDetails = [];
          let actuallyReleased = [];
          
          try {
            // Load active cart items from server to get authoritative mapping of boothId -> cartItemId
            let nextMap = {};
            let activeItems = [];
            try {
              if (currentUser?.uid && eventId && layoutId && sessionId) {
                activeItems = await loadActiveCartItems({ profileId: currentUser.uid, eventId, layoutId, sessionId });
                for (const it of activeItems) {
                  if (it.boothId) {
                    nextMap[it.boothId] = it.cartItemId || it.id;
                  }
                }
              }
            } catch (e) {
              console.error('[Timeout] Failed to load active cart items:', e);
              // Fall back to reading sessionStorage if load fails
              try {
                let currentMap = {};
                if (currentUser?.uid && eventId && layoutId) {
                  const key = `cartItemMap:${currentUser.uid}:${eventId}:${layoutId}`;
                  const stored = sessionStorage.getItem(key);
                  if (stored) currentMap = JSON.parse(stored);
                }
                nextMap = { ...currentMap };
              } catch (e2) {
                console.error('[Timeout] Failed to read cartItemMap from storage fallback:', e2);
                nextMap = {};
              }
            }

            // Determine booths to release using authoritative sources (activeItems, nextMap),
            // falling back to the captured `selectedCells` if nothing else is available.
            let boothsToRelease = [];
            if (Array.isArray(activeItems) && activeItems.length > 0) {
              boothsToRelease = Array.from(new Set(activeItems.map(it => String(it.boothId)).filter(Boolean)));
            }
            const mapKeys = Object.keys(nextMap || {}).map(String);
            if (mapKeys.length > 0) {
              boothsToRelease = Array.from(new Set([...(boothsToRelease || []), ...mapKeys]));
            }
            if (!boothsToRelease || boothsToRelease.length === 0) {
              boothsToRelease = Array.from(selectedCells).map(String);
            }

            // Build releasedDetails from authoritative activeItems first, then fallback to gridData
            releasedDetails = boothsToRelease.map(id => {
              const active = activeItems.find(it => String(it.boothId) === String(id));
              if (active) return { id: id, number: active.boothNumber || active.boothId };
              const gd = gridData.find(b => String(b.id) === String(id));
              if (gd) return { id: gd.id, number: gd.number };
              return { id, number: null };
            }).filter(Boolean);

            // actuallyReleased is populated in the loop
            for (const id of boothsToRelease) {
              try {
                let released = false;
                try {
                  console.info('[performTimeoutRelease] attempting releaseBoothRemote for', id);
                  await releaseBoothRemote({ eventId, layoutId, boothId: id, vendorId, createdByProfileId: currentUser?.uid, sessionId, triggeredBy: 'Cart', type: 'Expired' });
                  released = true;
                  console.info('[performTimeoutRelease] releaseBoothRemote succeeded for', id);
                } catch (err) {
                  console.error('[performTimeoutRelease] releaseBoothRemote failed for', id, err);
                }

                // Update local grid state regardless so UI reflects availability
                setGridData(prev => prev.map(c => c.id === id ? { ...c, status: 'Available', reservedByMe: false, pendingByMe: false } : c));

                const cartItemId = nextMap[id];
                if (cartItemId) {
                  try {
                    console.info('[performTimeoutRelease] updating cartItem to expired for', cartItemId, 'booth', id);
                    await updateCartItemAction(cartItemId, 'expired', {
                      triggeredByProfileIdOverride: currentUser.uid,
                      triggeredByOverride: 'cart-expired'
                    });
                    console.info('[performTimeoutRelease] updateCartItemAction succeeded for', cartItemId);
                  } catch (err) {
                    console.error('[performTimeoutRelease] updateCartItemAction failed for', cartItemId, err);
                  }
                  delete nextMap[id];
                  // consider cart item update as part of successful release
                  released = true;
                }

                if (released) {
                  // attempt to get booth number for display
                  const active = activeItems.find(it => String(it.boothId) === String(id));
                  if (active) {
                    actuallyReleased.push({ id, number: active.boothNumber || active.boothId });
                  } else {
                    const gd = gridData.find(b => String(b.id) === String(id));
                    actuallyReleased.push({ id, number: gd ? gd.number : null });
                  }
                }
              } catch (iterErr) {
                console.error('[performTimeoutRelease] Error processing booth', id, iterErr);
              }
            }
            // Persist remaining map even if some iterations failed
            persistCartItemMap(nextMap);
          } catch (e) {
            console.error('[performTimeoutRelease] Error releasing booths', e);
          } finally {
            setSelectedCells(new Set());
            setShowBoothRules(false);
            setRuleViolations([]);
            setRuleSuggestions([]);
            setRequiredBooths([]);
            try {
              const key = expiryStorageKey(currentUser?.uid, eventId, layoutId, sessionId);
              if (key) sessionStorage.removeItem(key);
            } catch {}
            setCartExpiryTs(null);
            setTimeLeftSeconds(0);
            // Prefer reporting the booths we actually released; fall back to attempted list
            const finalReleased = (Array.isArray(actuallyReleased) && actuallyReleased.length > 0) ? actuallyReleased : releasedDetails;
            console.info('[performTimeoutRelease] finalReleased for popup:', finalReleased);
            setTimeoutReleasedBooths(finalReleased);
            setTimeoutModalOpen(true);
            timeoutReleaseInProgress.current = false;
          }
        })();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartExpiryTs]); // ONLY depend on cartExpiryTs - all other values captured in closure

  const orphanPendingCleanupRunning = useRef(false);

  // Drive cart expiry from server-side reservation data and cleanup orphan pending rows.
  useEffect(() => {
    if (isPreview) return; // no timer in Preview Mode
    if (!currentUser?.uid || !vendorId || !eventId || !layoutId || !sessionId) return;
    const unsub = subscribeMyReservationsForSession({
      eventId,
      layoutId,
      vendorId,
      sessionId,
      onChange: (rows) => {
        const toMillis = (ts) => {
          try {
            if (!ts) return null;
            if (typeof ts.toMillis === 'function') return ts.toMillis();
            if (ts.seconds) return ts.seconds * 1000;
            const d = new Date(ts);
            return isNaN(d.getTime()) ? null : d.getTime();
          } catch { return null; }
        };

        const resolveExpiryMs = (row) => {
          const explicitExpiresMs = toMillis(row?.expiresAt);
          if (typeof explicitExpiresMs === 'number') return explicitExpiresMs;
          const createdMs = toMillis(row?.createdAt);
          if (hasValidShoppingCartWait && createdMs) {
            return createdMs + shoppingCartWaitMinutes * 60 * 1000;
          }
          return null;
        };

        // Consider only Pending holds that are still in the cart phase (not already in payment).
        // Rows with pendingType="payment" are owned by StripeCheckout — the cart timer must not
        // release or expire them.
        const pending = (rows || []).filter(r => r.status === 'Pending' && r.boothId && r.pendingType !== 'payment');
        if (pending.length === 0) {
          setCartExpiryTs(null);
          setTimeLeftSeconds(null);
          return;
        }
        const now = Date.now();

        // Cleanup orphan pending reservations (pending row exists but no active cart item).
        if (!orphanPendingCleanupRunning.current) {
          orphanPendingCleanupRunning.current = true;
          (async () => {
            try {
              const activeCartItems = await loadActiveCartItems({
                profileId: currentUser.uid,
                eventId,
                layoutId,
                sessionId,
              });
              const activeCartBoothIds = new Set((activeCartItems || []).map(it => String(it.boothId)).filter(Boolean));
              const orphanPending = pending.filter((p) => {
                const id = String(p.boothId);
                if (activeCartBoothIds.has(id)) return false;
                if (selectedCells.has(id)) return false;
                if (cartItemMap[id]) return false;
                const createdMs = toMillis(p.createdAt);
                if (typeof createdMs === 'number' && (Date.now() - createdMs) < 15000) return false;
                return true;
              });

              for (const p of orphanPending) {
                try {
                  await releaseBoothRemote({
                    eventId,
                    layoutId,
                    boothId: p.boothId,
                    vendorId,
                    createdByProfileId: currentUser?.uid,
                    sessionId,
                    triggeredBy: 'orphan-pending-no-cart-cleanup',
                  });
                  setGridData(prev => prev.map(c => c.id === p.boothId ? { ...c, status: 'Available', reservedByMe: false, pendingByMe: false } : c));
                } catch (err) {
                  console.error('[BoothSelection] Failed to release orphan pending booth', p.boothId, err);
                }
              }
            } catch (err) {
              console.error('[BoothSelection] Orphan pending cleanup failed', err);
            } finally {
              orphanPendingCleanupRunning.current = false;
            }
          })();
        }

        const activeExpiryMs = pending
          .map(resolveExpiryMs)
          .filter(v => typeof v === 'number' && v > now)
          .sort((a, b) => a - b);

        if (activeExpiryMs.length === 0) {
          setCartExpiryTs(null);
          setTimeLeftSeconds(null);
          return;
        }

        setCartExpiryTs(activeExpiryMs[0]);
      }
    });
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [isPreview, currentUser?.uid, vendorId, eventId, layoutId, sessionId, shoppingCartWaitMinutes, hasValidShoppingCartWait, selectedCells, cartItemMap]);

  return (
    <div className="grid-builder-container booth-selection-page">
      <div className="grid-builder-header">
        <div className="header-content">
          <div className="title-section" style={{ textAlign: 'left' }}>
            <h1 style={{ fontSize: '16px', marginBottom: '4px', lineHeight: '1.1' }}>
              <span style={{ color: '#f39c12', fontWeight: 700 }}>Vendor:</span> {vendorName || 'N/A'}{cartCollapsed && selectedCells.size > 0 ? ` (${selectedCells.size} selected)` : ''}
            </h1>
            <h1 style={{ 
              fontSize: '16px', 
              color: '#ecf0f1', 
              marginTop: '4px',
              marginBottom: '4px',
              fontWeight: 500,
              lineHeight: '1.15'
            }}>
              <span style={{ color: '#f39c12', fontWeight: 700 }}>Event:</span> {eventInfo?.name || 'N/A'}
            </h1>
            <h1 style={{ 
              fontSize: '16px', 
              color: '#ecf0f1', 
              marginTop: '4px',
              marginBottom: '4px',
              fontWeight: 500,
              lineHeight: '1.15'
            }}>
              <span style={{ color: '#f39c12', fontWeight: 700 }}>Layout:</span> {layoutName || 'N/A'}
            </h1>
            <p style={{ 
              fontSize: '12px', 
              color: '#ecf0f1', 
              marginTop: '2px',
              marginBottom: '4px',
              fontWeight: 500,
              lineHeight: '1.2'
            }}>
              <span style={{ color: '#f39c12', fontWeight: 700 }}>Time:</span> {currentDateTime.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })} • {currentDateTime.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit' 
              })}
            </p>
            {isPreview && (
              <div className="profile-hint" style={{ color: '#f1c40f', fontWeight: 600, marginTop: '8px' }}>
                Preview mode — no live holds or purchases
              </div>
            )}
          </div>
          <div className="header-actions">
            {/* Prominent countdown timer next to Back button, visible only when cart is expanded */}
            {!isPreview && typeof timeLeftSeconds === 'number' && selectedCells.size > 0 && (
              <div
                className="header-timer"
                aria-live="polite"
                title="Cart hold time remaining"
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 10,
                  marginRight: 12
                }}
              >
                <span
                  className={`timer-value ${timeLeftSeconds <= 60 ? 'flash-yellow' : ''}`}
                  style={{
                    fontWeight: 800,
                    fontSize: 34,
                    color: '#f1c40f'
                  }}
                >
                  ⏳ {Math.floor(timeLeftSeconds / 60).toString().padStart(2, '0')}
                  :{(timeLeftSeconds % 60).toString().padStart(2, '0')}
                </span>
                <span
                  className="timer-label"
                  style={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: '#ffffff'
                  }}
                >
                  minutes left to complete your transaction
                </span>
              </div>
            )}
            {cartCollapsed && (
              <button 
                onClick={() => setCartCollapsed(false)}
                className="back-btn"
                title="Show Cart"
              >
                🛒 Show Cart{selectedCells.size > 0 ? ` (${selectedCells.size})` : ''}
              </button>
            )}
            <button 
              onClick={handleBack}
              className="back-btn"
              title={backTo === '/' ? 'Back to Home' : 'Back to Layout'}
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* Embedded Shopping Cart - collapsible */}
      {!cartCollapsed && (
        <BoothShoppingCart
          selectedBooths={Array.from(selectedCells)}
          gridData={gridData}
          onRemoveBooth={handleRemoveFromCart}
          onClearCart={handleClearCart}
          rulesConfig={rulesConfig}
          show={true}
          onClose={() => {}}
          getLevelPricing={getLevelPricing}
          customLevels={customLevels}
          getLevelColor={getLevelColor}
          variant="embedded"
          onCollapse={() => setCartCollapsed(true)}
          timeLeftSeconds={timeLeftSeconds}
          shoppingCartWaitMinutes={shoppingCartWaitMinutes}
          hideCheckout={isPreview}
          // Pass the current eventId explicitly so cart can scope application approval check
          eventId={eventId}
          eventName={eventInfo?.name || ''}
          layoutId={layoutId}
          paymentWaitMinutes={paymentWaitMinutes}
        />
      )}

      {/* Initialization banner while we wait for first live overlay */}
      {!isPreview && !reservationsPrimed && (
        <div
          role="status"
          aria-live="polite"
          style={{
            padding: '6px 10px',
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: 4,
            margin: '8px 12px',
            fontSize: 13,
            color: '#8c6d1f',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <span style={{ fontWeight: 700 }}>Initializing live availability…</span>
          <span style={{ opacity: 0.85 }}>Please wait a moment; booth statuses are syncing.</span>
        </div>
      )}

      <div className="grid-container" style={{ position: 'relative' }}>
        {/* Surroundings rendered inside the white grid area (top/left/right/bottom) */}
        <div ref={gridWrapperRef} className="grid-scroll-wrapper">
          {/* Top label inside the white grid area */}
          {surroundings?.top && surroundings.top.length > 0 && (
            <div
              className="surround-inside surround-top-inside"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 0',
                backgroundColor: surroundingsBgColor || 'transparent',
                width: gridInnerSize.width ? `${gridInnerSize.width}px` : '100%',
                marginLeft: gridInnerSize.left != null ? `${gridInnerSize.left}px` : '0',
                borderRadius: 6,
                boxSizing: 'border-box'
              }}
            >
              <span style={{ display: 'block', width: '100%', textAlign: 'center', color: surroundingsTextColor || 'rgba(0,0,0,0.85)', fontSize: (surroundingsFontSize ? `${surroundingsFontSize}px` : 14) }}>{surroundings.top.join(', ')}</span>
            </div>
          )}

          <div className="surround-inner-main" style={{ display: 'flex', alignItems: 'stretch', gap: 12 }}>
            {/* Left label inside the white grid area */}
            {surroundings?.left && surroundings.left.length > 0 && (
              <div className="surround-inside surround-left-inside" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 8px', backgroundColor: surroundingsBgColor || 'transparent', minWidth: `${sideColumnWidth}px`, height: gridInnerSize.height ? `${gridInnerSize.height}px` : 'auto' }}>
                <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', textAlign: 'left', color: surroundingsTextColor || 'rgba(0,0,0,0.85)', fontSize: (surroundingsFontSize ? `${surroundingsFontSize}px` : 13) }}>{surroundings.left.join(', ')}</div>
              </div>
            )}

            <div 
              ref={dynamicGridRef}
              className="dynamic-grid"
              style={{
                gridTemplateColumns: `repeat(${columns}, ${displayCellSize}px)`,
                gridTemplateRows: `repeat(${rows}, ${displayCellSize}px)`,
                gap: `${cellPadding}px`,
                boxSizing: 'border-box',
                overflowX: 'auto',
                maxWidth: `calc(100% - ${sideColumnWidth * 2}px)`,
                minWidth: 0,
              }}
            >
              {gridData.map((cell) => (
                <GridCell
                  key={cell.id}
                  cellData={cell}
                  onClick={handleCellClick}
                  isSelected={selectedCells.has(cell.id)}
                  isRequired={requiredBooths.includes(cell.id)}
                  cellSize={displayCellSize}
                  fontSize={fontSize}
                  customLevels={customLevels}
                  rulesConfig={rulesConfig}
                  cartActionBusy={cartActionBusy}
                />
              ))}
            </div>

            {/* Right label inside the white grid area */}
            {surroundings?.right && surroundings.right.length > 0 && (
              <div className="surround-inside surround-right-inside" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 8px', backgroundColor: surroundingsBgColor || 'transparent', minWidth: `${sideColumnWidth}px`, height: gridInnerSize.height ? `${gridInnerSize.height}px` : 'auto' }}>
                <div style={{ writingMode: 'vertical-rl', textAlign: 'left', color: surroundingsTextColor || 'rgba(0,0,0,0.85)', fontSize: (surroundingsFontSize ? `${surroundingsFontSize}px` : 13) }}>{surroundings.right.join(', ')}</div>
              </div>
            )}
          </div>

          {/* Bottom label inside the white grid area */}
          {surroundings?.bottom && surroundings.bottom.length > 0 && (
            <div
              className="surround-inside surround-bottom-inside"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 0',
                backgroundColor: surroundingsBgColor || 'transparent',
                width: gridInnerSize.width ? `${gridInnerSize.width}px` : '100%',
                marginLeft: gridInnerSize.left != null ? `${gridInnerSize.left}px` : '0',
                borderRadius: 6,
                boxSizing: 'border-box'
              }}
            >
              <span style={{ display: 'block', width: '100%', textAlign: 'center', color: surroundingsTextColor || 'rgba(0,0,0,0.85)', fontSize: (surroundingsFontSize ? `${surroundingsFontSize}px` : 14) }}>{surroundings.bottom.join(', ')}</span>
            </div>
          )}
        </div>
        {/* Click-blocking overlay until primed (non-Preview) */}
        {!isPreview && !reservationsPrimed && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'transparent',
              pointerEvents: 'auto',
            }}
          />
        )}
      </div>

      <BoothRulesPanel
        violations={ruleViolations}
        suggestions={ruleSuggestions}
        onApplySuggestions={handleApplySuggestions}
        onRemoveBooths={async (boothIds) => {
          // Remove specified booths: release reservations & update cart items
          const nextSet = new Set(selectedCells);
          const nextMap = { ...cartItemMap };
          // Process all provided ids if they are currently selected OR pending-by-me on grid
          const idsToProcess = boothIds.filter(id => {
            const cell = gridData.find(c => c.id === id);
            return nextSet.has(id) || (cell && cell.status === 'Pending' && cell.pendingByMe);
          });
          if (idsToProcess.length === 0) return;
          for (const id of idsToProcess) {
            // Drop from selection if present
            if (nextSet.has(id)) nextSet.delete(id);
            try {
              if (!isPreview && currentUser?.uid && vendorId && eventId && layoutId && sessionId) {
                await releaseBoothRemote({ eventId, layoutId, boothId: id, vendorId, createdByProfileId: currentUser?.uid, sessionId, triggeredBy: 'Cart', type: 'Removed' });
                // Optimistically mark Available to clear any pending-by-me badge immediately
                setGridData(prev => prev.map(c => c.id === id ? { ...c, status: 'Available', reservedByMe: false, pendingByMe: false } : c));
              }
            } catch {}
            const mapped = nextMap[id];
            if (mapped) {
              try { 
                await updateCartItemAction(mapped, 'removed', {
                  triggeredByProfileIdOverride: currentUser?.uid,
                  triggeredByOverride: 'cart-item-removed'
                }); 
              } catch {}
              delete nextMap[id];
            }
          }
          persistCartItemMap(nextMap);
          setSelectedCells(nextSet);
          const remaining = Array.from(nextSet);
          const validation = validateBoothSelection(remaining);
          if (validation.violations.length === 0 && validation.suggestions.length === 0) {
            setShowBoothRules(false);
          }
        }}
        onDismiss={() => setShowBoothRules(false)}
        selectedCount={selectedCells.size}
        show={showBoothRules}
      />
      {timeoutModalOpen && (
        <div className="popup-overlay" onClick={() => { setTimeoutModalOpen(false); setTimeoutReleasedBooths([]); }}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="popup-header">
              <h3>Cart Hold Expired</h3>
              <button className="close-btn" onClick={() => { setTimeoutModalOpen(false); setTimeoutReleasedBooths([]); }}>×</button>
            </div>
            <div className="popup-body">
              <p>You have run out of time to complete the booth reservation.</p>
              {Array.isArray(timeoutReleasedBooths) && timeoutReleasedBooths.length > 0 && (
                <div style={{ margin: '0.5rem 0 0.75rem' }}>
                  <strong>These booths will now be released:</strong>
                  <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                    {timeoutReleasedBooths.map(b => (
                      <li key={b.id}>#{b.number}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p>You can also choose them again.</p>
            </div>
            <div className="edit-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="apply-btn compact-btn" onClick={() => { setTimeoutModalOpen(false); setTimeoutReleasedBooths([]); }}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Cart is embedded above */}
    </div>
  );
};

export default BoothSelection;
